import React, { useEffect, useRef, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { EmptyState } from "@/components/EmptyState";
import { ProductCard } from "@/components/ProductCard";
import { ProductGridSkeleton } from "@/components/Skeleton";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useBackButton } from "@/lib/hooks";
import { fetchRecipe, fetchRecipes, mediaUrl, type Recipe } from "@/lib/api";
import { haptic } from "@/lib/telegram";
import type { Product } from "@/types";

const fmtTime = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
const total = (r: Recipe) => r.steps.reduce((s, x) => s + x.seconds, 0);

export function RecipeCard({ r }: { r: Recipe }) {
  const { t } = useI18n();
  const { navigate } = useRouter();
  return (
    <button className="recipe-card" onClick={() => navigate({ name: "recipe", slug: r.slug })}>
      <span className="recipe-card__img">{r.coverImage ? <img src={mediaUrl(r.coverImage, 400)} alt="" /> : <span className="recipe-card__method">{t(`recipes.methods.${r.method}`)}</span>}</span>
      <span className="recipe-card__body">
        <span className="eyebrow">{t(`recipes.methods.${r.method}`)}</span>
        <b>{r.title}</b>
        <small>
          {r.coffeeGrams} {t("recipes.g")} · {r.waterMl} {t("recipes.ml")} · {fmtTime(total(r))}
        </small>
      </span>
    </button>
  );
}

export function RecipesPage() {
  const { t, locale } = useI18n();
  const { goBack } = useRouter();
  useBackButton(goBack);
  const [list, setList] = useState<Recipe[] | null>(null);
  useEffect(() => {
    fetchRecipes().then(setList).catch(() => setList([]));
  }, [locale]);
  return (
    <>
      <TopBar title={t("recipes.title")} onBack={goBack} />
      <main className="page page--no-nav">
        <p className="lead-sub" style={{ marginTop: 0 }}>{t("recipes.intro")}</p>
        {list === null ? (
          <ProductGridSkeleton count={2} />
        ) : list.length === 0 ? (
          <EmptyState art="generic" title={t("recipes.empty")} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {list.map((r) => (
              <RecipeCard key={r.id} r={r} />
            ))}
          </div>
        )}
      </main>
    </>
  );
}

/** Step-by-step brewing with a big timer: auto-advance, a buzz on each step. */
export function RecipePage({ slug }: { slug: string }) {
  const { t, locale } = useI18n();
  const { goBack } = useRouter();
  useBackButton(goBack);
  const [data, setData] = useState<{ recipe: Recipe; products: Product[] } | null | undefined>(undefined);
  const [step, setStep] = useState(-1); // -1 = not started
  const [left, setLeft] = useState(0);
  const [running, setRunning] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    fetchRecipe(slug).then(setData).catch(() => setData(null));
  }, [slug, locale]);

  // Tick once a second while running.
  useEffect(() => {
    if (!running) return;
    timer.current = window.setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [running]);

  // When a timed step reaches zero: buzz and move to the next step.
  useEffect(() => {
    if (!running || left > 0 || !data) return;
    const steps = data.recipe.steps;
    haptic("success");
    const next = step + 1;
    if (next >= steps.length) {
      setRunning(false);
      setStep(steps.length);
      return;
    }
    setStep(next);
    setLeft(steps[next].seconds);
    if (!steps[next].seconds) setRunning(false);
  }, [left, running]);

  if (data === undefined) return (<><TopBar title="" onBack={goBack} /><main className="page page--no-nav"><ProductGridSkeleton count={1} /></main></>);
  if (!data) return (<><TopBar title="" onBack={goBack} /><main className="page page--no-nav"><EmptyState art="generic" title={t("recipes.notFound")} /></main></>);

  const r = data.recipe;
  const finished = step >= r.steps.length;
  const cur = step >= 0 && !finished ? r.steps[step] : null;
  const start = () => {
    haptic("light");
    setStep(0);
    setLeft(r.steps[0].seconds);
    setRunning(r.steps[0].seconds > 0);
  };
  const nextManual = () => {
    const next = step + 1;
    if (next >= r.steps.length) return setStep(r.steps.length);
    setStep(next);
    setLeft(r.steps[next].seconds);
    setRunning(r.steps[next].seconds > 0);
  };

  return (
    <>
      <TopBar title={r.title} onBack={goBack} />
      <main className="page page--no-nav">
        {r.coverImage && <img className="recipe-cover" src={mediaUrl(r.coverImage, 800)} alt="" />}
        <span className="eyebrow">{t(`recipes.methods.${r.method}`)}</span>
        <h1 className="product-detail__title">{r.title}</h1>
        {r.description && <p className="product-detail__desc" style={{ marginTop: 0 }}>{r.description}</p>}
        <div className="attr-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          <div><span>{t("recipes.coffee")}</span><strong>{r.coffeeGrams} {t("recipes.g")}</strong></div>
          <div><span>{t("recipes.water")}</span><strong>{r.waterMl} {t("recipes.ml")}</strong></div>
          <div><span>{t("recipes.temp")}</span><strong>{r.tempC ? `${r.tempC}°C` : "—"}</strong></div>
          <div><span>{t("recipes.grind")}</span><strong>{r.grind || "—"}</strong></div>
        </div>

        <div className="brew">
          {step < 0 ? (
            <>
              <div className="brew__total">{fmtTime(total(r))}</div>
              <p>{t("recipes.ready")}</p>
              <button className="btn btn--primary" onClick={start}>{t("recipes.start")}</button>
            </>
          ) : finished ? (
            <>
              <div className="brew__total">☕</div>
              <p>{t("recipes.done")}</p>
              <button className="btn btn--ghost" onClick={() => { setStep(-1); setRunning(false); }}>{t("recipes.again")}</button>
            </>
          ) : (
            <>
              <span className="eyebrow">{t("recipes.step", { n: step + 1, total: r.steps.length })}</span>
              <div className="brew__time">{cur!.seconds ? fmtTime(left) : "—"}</div>
              <p className="brew__text">{cur!.text}</p>
              {cur!.pourToMl ? <p className="brew__pour">{t("recipes.pourTo", { ml: cur!.pourToMl })}</p> : null}
              <div className="brew__bar"><div style={{ width: `${cur!.seconds ? ((cur!.seconds - left) / cur!.seconds) * 100 : 100}%` }} /></div>
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                {cur!.seconds > 0 && <button className="btn btn--ghost" style={{ flex: 1 }} onClick={() => setRunning((x) => !x)}>{running ? t("recipes.pause") : t("recipes.resume")}</button>}
                <button className="btn btn--primary" style={{ flex: 1 }} onClick={nextManual}>{t("recipes.next")}</button>
              </div>
            </>
          )}
        </div>

        <ol className="brew-steps">
          {r.steps.map((s, i) => (
            <li key={i} data-state={i < step || finished ? "done" : i === step ? "now" : "todo"}>
              <span>{s.text}</span>
              {s.seconds > 0 && <small>{fmtTime(s.seconds)}</small>}
            </li>
          ))}
        </ol>

        {data.products.length > 0 && (
          <>
            <div className="section-heading"><span className="diamond" />{t("recipes.forThis")}</div>
            <div className="rail">
              {data.products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </>
        )}
      </main>
    </>
  );
}
